import { CUSTOM_ELEMENTS_SCHEMA, ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { A11yModule } from '@angular/cdk/a11y';
import { AddressInputComponent } from './components/address-input/address-input.component';
import { AssurancePretComponent } from '../assurance-pret/assurance-pret.component';
import { BackToTopComponent } from './components/back-to-top/back-to-top.component';
import { BlurOnMouseClickDirective } from './directives/blur-on-mouse-click.directive';
import { BoiteAideComponent } from './components/boite-aide/boite-aide.component';
import { BoiteContactComponent } from './components/boite-contact/boite-contact.component';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { CapsuleConseilComponent } from './components/capsule-conseil/capsule-conseil.component';
import { CapsuleConseilReduceComponent } from './components/capsule-conseil-reduce/capsule-conseil-reduce.component';
import { CardComponent } from './components/card/card.component';
import { CardContactComponent } from './components/card-contact/card-contact.component';
import { CardNextStepCompactComponent } from './components/card-next-step/card-next-step-compact/card-next-step-compact.component';
import { CardNextStepComponent } from './components/card-next-step/card-next-step.component';
import { CarteProprieteListeComponent } from '../propriete/components/carte-propriete-liste/carte-propriete-liste.component';
import { CheminAbsoluStatiquePipe } from './pipes/chemin-absolu-statique.pipe';
import { CommonModule } from '@angular/common';
import { ContentPreautorisationAccepteeDashboardComponent } from './components/content-preautorisation-acceptee-dashboard/content-preautorisation-acceptee-dashboard.component';
import { ContentPreautorisationDashboardComponent } from './components/content-preautorisation-dashboard/content-preautorisation-dashboard.component';
import { CountUpModule } from 'ngx-countup';
import { CurrencyInputComponent } from './components/currency-input/currency-input.component';
import { CurrencyStringComponent } from './components/currency-string/currency-string.component';
// Pipes
import { DatePipe } from './pipes/date.pipe';
import { DebugMenuComponent } from './components/debug-menu/debug-menu.component';
import { DesjardinsCurrencyPipe } from './pipes/dd-currency.pipe';
import { DialogAnnulationComponent } from './components/dialog-annulation/dialog-annulation.component';
import { DialogAnnulerModificationComponent } from './components/dialog-annuler-modification/dialog-annuler-modification.component';
// Components
import { DialogAnnulerRendezvousComponent } from './components/dialog-annuler-rendezvous/dialog-annuler-rendezvous.component';
import { DialogCloseButtonComponent } from './components/dialog-close-button/dialog-close-button.component';
import { DialogComplexComponent } from './components/dialog-complex/dialog-complex.component';
import { DialogQuitterPageComponent } from './components/dialog-quitter-page/dialog-quitter-page.component';
import { DialogSimpleComponent } from './components/dialog-simple/dialog-simple.component';
import { DialogSimpleConfirmationComponent } from './components/dialog-simple-confirmation/dialog-simple-confirmation.component';
import { DialogSupprimerProprieteComponent } from './components/dialog-supprimer-propriete/dialog-supprimer-propriete.component';
import { DisabledIfConseillerDirective } from './directives/disabled-if-conseiller/disabled-if-conseiller.directive';
import { DsdLibraryModule } from '@dsd/library-angular/dist';
import { DynamicBackgroundImageDirective } from './directives/dynamicBackgroundImage/dynamic-background-image.directive';
import { ErrorMessageValidationComponent } from './components/error-message-validation/error-message-validation.component';
import { EscapeHtmlPipe } from './pipes/keep-html.pipe';
import { FaqComponent } from './components/faq/faq.component';
import { FaqModalComponent } from './components/faq/faq-modal/faq-modal.component';
// Directives
import { FeatureToggleDirective } from '../core/directives/feature-toggle.directive';
import { FocusOnInitDirective } from './directives/focus-on-init.directive';
import { FrequenceSelectComponent } from './components/frequence-select/frequence-select.component';
import { GreenLabelComponent } from './components/green-label/green-label.component';
import { HabitationPageContainerComponent } from './containers/habitation-page-container/habitation-page-container.component';
import { HeroBannerComponent } from './components/hero-banner/hero-banner.component';
import { HeroContainerComponent } from './components/hero-container/hero-container.component';
import { HourFloorMinutePipe } from './pipes/hour-floor-minute.pipe';
import { HourPipe } from './pipes/hour.pipe';
import { HrefDirective } from './directives/custom-href/href.directive';
// Modules
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { InViewportModule } from 'ng-in-viewport';
import { LibDsdExtensionModule } from '../lib-dsd-ext/lib-dsd-extension.module';
import { LocalisationRepresentantApiService } from './services/localisation-representant-api.service';
import { LocalisationRepresentantAutocompleteComponent } from './components/localisation-representant-autocomplete/localisation-representant-autocomplete.component';
import { LocalisationRepresentantAutocompleteService } from './services/localisation-representant-autocomplete.service';
import { MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../material.module';
import { NgxCurrencyDirective } from 'ngx-currency';
import { NgxScrollToFirstInvalidDirective } from './directives/ngxScrollToFirstInvalid/ngxScrollToFirstInvalid.directive';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { NotificationModule } from '../notification/notification.module';
import { OpenModalService } from './services/open-modal/open-modal.service';
import { OptionBoxComponent } from './components/option-box/option-box.component';
import { PercentPipe } from './pipes/percent.pipe';
import { NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';

// Services
import { PersonneApiService } from './services/personne-api.service';
import { PhoneNumberPipe } from './pipes/phone-number.pipe';
import { PopupCapaciteErrorComponent } from './components/popups/popup-capacite-error/popup-capacite-error.component';
import { PopupContainerComponent } from './components/popups/popup-container/popup-container.component';
import { PopupPhotoErrorComponent } from './components/popups/popup-photo-error/popup-photo-error.component';
import { PopupProprieteErrorComponent } from './components/popups/popup-propriete-error/popup-propriete-error.component';
import { PortalModule } from '@angular/cdk/portal';
import { ProgressBarComponent } from './components/progress-bar/progress-bar.component';
import { PromoBannerComponent } from './components/promo-banner/promo-banner.component';
import { RouterModule } from '@angular/router';
import { SidenavComponent } from './components/sidenav/sidenav.component';
import { StaticContainerComponent } from './containers/static-container/static-container.component';
import { StatusBoxClockComponent } from './components/status-box-clock/status-box-clock.component';
import { StatusBoxProcessingComponent } from './components/status-box-processing/status-box-processing.component';
import { StatusBoxSuccessComponent } from './components/status-box-success/status-box-success.component';
import { StatusPreautorisationComponent } from './components/status-preautorisation/status-preautorisation.component';
import { SubFooterComponent } from './components/sub-footer/sub-footer.component';
import { TNRService } from './services/tnr.service';
import { TextualDatePipe } from './pipes/textual-date.pipe';
import { ThermometreCapaciteComponent } from './components/thermometre-capacite/thermometre-capacite.component';
import { TiroirComponent } from './components/tiroir/tiroir.component';
import { TooltipComponent } from './components/tooltip/tooltip.component';
import { TooltipDesktopComponent } from './components/tooltip/tooltip-desktop/tooltip-desktop.component';
import { TooltipMobileComponent } from './components/tooltip/tooltip-mobile/tooltip-mobile.component';
import { TotalRecapComponent } from './components/total-recap/total-recap.component';
import { TranslateModule } from '@ngx-translate/core';
import { UploadApiService } from './services/upload-api.service';

export const COMPONENTS = [
  DialogAnnulerRendezvousComponent,
  FrequenceSelectComponent,
  CurrencyStringComponent,
  PopupContainerComponent,
  DialogSupprimerProprieteComponent,
  CardNextStepComponent,
  CardNextStepCompactComponent,
  PopupCapaciteErrorComponent,
  SubFooterComponent,
  PopupProprieteErrorComponent,
  PopupPhotoErrorComponent,
  BreadcrumbComponent,
  StaticContainerComponent,
  CapsuleConseilComponent,
  BoiteContactComponent,
  FeatureToggleDirective,
  HabitationPageContainerComponent,
  HeroBannerComponent,
  OptionBoxComponent,
  GreenLabelComponent,
  CardContactComponent,
  CurrencyInputComponent,
  LocalisationRepresentantAutocompleteComponent,
  TooltipDesktopComponent,
  BackToTopComponent,
  ProgressBarComponent,
  AddressInputComponent,
  ThermometreCapaciteComponent,
  DialogAnnulationComponent,
  DialogAnnulerModificationComponent,
  TooltipMobileComponent,
  TooltipComponent,
  ContentPreautorisationDashboardComponent,
  ContentPreautorisationAccepteeDashboardComponent,
  StatusPreautorisationComponent,
  CapsuleConseilReduceComponent,
  CarteProprieteListeComponent,
  AssurancePretComponent,
  ErrorMessageValidationComponent,
  StatusBoxProcessingComponent,
  StatusBoxSuccessComponent,
  StatusBoxClockComponent,
  DialogSimpleComponent,
  DialogSimpleConfirmationComponent,
  DialogCloseButtonComponent,
  DialogComplexComponent,
  DialogQuitterPageComponent,
  TiroirComponent,
  SidenavComponent,
  FaqComponent,
  FaqModalComponent,
  PromoBannerComponent,
  TotalRecapComponent,
  BoiteAideComponent,
  CardComponent,
  DebugMenuComponent,
  HeroContainerComponent
];

const directives = [
  DatePipe,
  PercentPipe,
  NgxScrollToFirstInvalidDirective,
  DesjardinsCurrencyPipe,
  FocusOnInitDirective,
  HrefDirective,
  TextualDatePipe,
  DisabledIfConseillerDirective,
  EscapeHtmlPipe,
  HourPipe,
  HourFloorMinutePipe,
  PhoneNumberPipe,
  CheminAbsoluStatiquePipe,
  BlurOnMouseClickDirective,
  DynamicBackgroundImageDirective
];
@NgModule({
  declarations: [...COMPONENTS, ...directives],
  exports: [...COMPONENTS, ...directives, ReactiveFormsModule, A11yModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    A11yModule,
    CommonModule,
    RouterModule,
    TranslateModule,
    FormsModule,
    MaterialModule,
    ReactiveFormsModule,
    InViewportModule,
    CountUpModule,
    NgxCurrencyDirective,
    NgxTippyModule,
    NotificationModule,
    LibDsdExtensionModule,
    DsdLibraryModule,
    PortalModule,
    NgxMaskDirective,
    NgxMaskPipe
  ],
  providers: [
    PersonneApiService,
    UploadApiService,
    LocalisationRepresentantAutocompleteService,
    LocalisationRepresentantApiService,
    OpenModalService,
    {
      provide: MatDialogRef,
      useValue: {}
    },
    TNRService,
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class SharedModule {
  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule
    };
  }
}
